import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from './supabase';

// Only meaningful for admin-tier users -- a gestor already gets her own
// single-item branch/name from `profile` directly (see visibleBranches/
// visibleManagers in App.js). Skips the query entirely otherwise, since
// RLS wouldn't return anything useful to her anyway.
export const useOrgRoster = (isAdmin) => {
  const [rawBranches, setRawBranches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isAdmin) {
      setRawBranches([]);
      setStaff([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const [branchesRes, profilesRes] = await Promise.all([
      supabase.from('branches').select('id, nombre').order('nombre'),
      supabase.from('profiles').select('id, email, display_name, role, branch_id').order('display_name')
    ]);

    if (branchesRes.error) {
      console.error('Error loading branches:', branchesRes.error);
      setRawBranches([]);
    } else {
      setRawBranches(branchesRes.data || []);
    }

    if (profilesRes.error) {
      console.error('Error loading staff roster:', profilesRes.error);
      setStaff([]);
    } else {
      setStaff(profilesRes.data || []);
    }

    setLoading(false);
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  // Plain name lists, for the many dropdowns/lists that only ever needed
  // a name -- kept here (not re-derived at each call site) so App.js,
  // Settings.js, and Customers.js all read the exact same roster.
  // Memoized so consumers get a stable reference across renders that
  // don't touch rawBranches/staff (e.g. GoalsPerformanceTracker's own
  // useMemos key off this array's identity).
  const branches = useMemo(() => rawBranches.map((b) => b.nombre), [rawBranches]);
  const gestores = useMemo(
    () => staff.filter((p) => p.role === 'gestor').map((p) => p.display_name).filter(Boolean),
    [staff]
  );
  const supervisors = useMemo(
    () => staff.filter((p) => p.role === 'supervisor').map((p) => p.display_name).filter(Boolean),
    [staff]
  );

  return { branches, rawBranches, gestores, supervisors, staff, loading, refresh: load };
};
